import { useState, useCallback, useRef } from 'react';
import { TranslationService } from '../core/TranslationService';
import type { TranslatableEvent } from '../../types/translation';

interface UseEventTranslationOptions {
  apiKey?: string;
  targetLanguage?: string;
}

interface TranslationCache {
  textCache: Map<string, string>;
  eventCache: Map<string, TranslatableEvent>;
}

function getBrowserLanguage(): string {
  const browserLang = navigator.language || (navigator as Navigator & { userLanguage?: string }).userLanguage;
  if (!browserLang) return 'en'; // Default to English if browser language not detected
  
  const langCode = browserLang.split('-')[0].toLowerCase();
  return langCode === 'en' ? 'en' : langCode; // Keep English as English, use detected language otherwise
}

// Persistent cache shared across all hook instances (like singleton pattern in codemap)
const globalTranslationCache: TranslationCache = {
  textCache: new Map<string, string>(),
  eventCache: new Map<string, TranslatableEvent>(),
};

export function useEventTranslation(options?: UseEventTranslationOptions) {
  const [isTranslating, setIsTranslating] = useState(false);
  const [isTranslatedView, setIsTranslatedView] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const serviceRef = useRef<TranslationService | null>(null);

  const getService = useCallback(() => {
    if (!serviceRef.current && options?.apiKey) {
      const targetLang = options.targetLanguage || getBrowserLanguage();
      serviceRef.current = new TranslationService({
        apiKey: options.apiKey,
        targetLanguage: targetLang,
      });
    }
    return serviceRef.current;
  }, [options?.apiKey, options?.targetLanguage]);

  const translateEvents = useCallback(async (events: TranslatableEvent[]): Promise<TranslatableEvent[]> => {
    const service = getService();
    if (!service) {
      setError('Translation service not initialized. API key required.');
      return events;
    }

    setIsTranslating(true);
    setError(null);

    try {
      const translatedEvents = await Promise.all(
        events.map(async (event): Promise<TranslatableEvent> => {
          const cacheKey = `${event.id}`;
          
          // Check global event cache first (like codemap Location 4b)
          if (globalTranslationCache.eventCache.has(cacheKey)) {
            return globalTranslationCache.eventCache.get(cacheKey)!;
          }

          const requests = [];
          if (event.title) {
            // Check text cache before adding request
            const cachedTitle = globalTranslationCache.textCache.get(event.title);
            if (!cachedTitle) {
              requests.push({ key: 'title', text: event.title });
            }
          }
          if (event.organizer) {
            const cachedOrganizer = globalTranslationCache.textCache.get(event.organizer);
            if (!cachedOrganizer) {
              requests.push({ key: 'organizer', text: event.organizer });
            }
          }
          if (event.venue) {
            const cachedVenue = globalTranslationCache.textCache.get(event.venue);
            if (!cachedVenue) {
              requests.push({ key: 'venue', text: event.venue });
            }
          }
          if (event.venueAddress?.addressLocality) {
            const cachedLocality = globalTranslationCache.textCache.get(event.venueAddress.addressLocality);
            if (!cachedLocality) {
              requests.push({ key: 'venueAddress.addressLocality', text: event.venueAddress.addressLocality });
            }
          }
          if (event.venueAddress?.addressRegion) {
            const cachedRegion = globalTranslationCache.textCache.get(event.venueAddress.addressRegion);
            if (!cachedRegion) {
              requests.push({ key: 'venueAddress.addressRegion', text: event.venueAddress.addressRegion });
            }
          }

          const translatedEvent: TranslatableEvent = { ...event };

          // Use cached translations if available
          if (event.title) {
            const cachedTitle = globalTranslationCache.textCache.get(event.title);
            if (cachedTitle) {
              translatedEvent.title = cachedTitle;
            }
          }
          if (event.organizer) {
            const cachedOrganizer = globalTranslationCache.textCache.get(event.organizer);
            if (cachedOrganizer) {
              translatedEvent.organizer = cachedOrganizer;
            }
          }
          if (event.venue) {
            const cachedVenue = globalTranslationCache.textCache.get(event.venue);
            if (cachedVenue) {
              translatedEvent.venue = cachedVenue;
            }
          }
          if (event.venueAddress?.addressLocality) {
            const cachedLocality = globalTranslationCache.textCache.get(event.venueAddress.addressLocality);
            if (cachedLocality) {
              translatedEvent.venueAddress = {
                ...translatedEvent.venueAddress,
                addressLocality: cachedLocality,
              };
            }
          }
          if (event.venueAddress?.addressRegion) {
            const cachedRegion = globalTranslationCache.textCache.get(event.venueAddress.addressRegion);
            if (cachedRegion) {
              translatedEvent.venueAddress = {
                ...translatedEvent.venueAddress,
                addressRegion: cachedRegion,
              };
            }
          }

          // Only call API for uncached texts
          if (requests.length > 0) {
            const translations = await service.translate(requests);
            
            translations.forEach(result => {
              // Store in text cache (like codemap Location 4f)
              globalTranslationCache.textCache.set(result.original, result.translated);
              
              if (result.key === 'title') {
                translatedEvent.title = result.translated;
              } else if (result.key === 'organizer') {
                translatedEvent.organizer = result.translated;
              } else if (result.key === 'venue') {
                translatedEvent.venue = result.translated;
              } else if (result.key === 'venueAddress.addressLocality') {
                translatedEvent.venueAddress = {
                  ...translatedEvent.venueAddress,
                  addressLocality: result.translated,
                };
              } else if (result.key === 'venueAddress.addressRegion') {
                translatedEvent.venueAddress = {
                  ...translatedEvent.venueAddress,
                  addressRegion: result.translated,
                };
              }
            });
          }

          // Cache the complete translated event
          globalTranslationCache.eventCache.set(cacheKey, translatedEvent);
          return translatedEvent;
        })
      );

      return translatedEvents;
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Translation failed';
      console.error('Translation failed:', err);
      setError(errorMessage);
      return events;
    } finally {
      setIsTranslating(false);
    }
  }, [getService]);

  const toggleTranslation = useCallback(async (
    originalEvents: TranslatableEvent[],
    onTranslated: (events: TranslatableEvent[]) => void
  ) => {
    if (isTranslatedView) {
      setIsTranslatedView(false);
      onTranslated(originalEvents);
    } else {
      const translated = await translateEvents(originalEvents);
      setIsTranslatedView(true);
      onTranslated(translated);
    }
  }, [isTranslatedView, translateEvents]);

  const reset = useCallback(() => {
    setIsTranslatedView(false);
    setError(null);
    globalTranslationCache.eventCache.clear();
    globalTranslationCache.textCache.clear();
  }, []);

  return {
    isTranslating,
    isTranslatedView,
    error,
    toggleTranslation,
    reset,
  };
}
